import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PatternFormat } from "react-number-format";
import { Alert, AlertTitle, InputAdornment, MenuItem } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import Input from "@/components/Tags/Input";
import CallIcon from "@mui/icons-material/Call";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LoadingButton from "@/components/LoadingButton";
import axios from "@/lib/axios";
import Button from "@/components/Tags/Form/Button";
import Turnstile from "@/components/Tags/Turnstile";

const defaultValues = {
    name: "",
    mobile_no: "",
    email: "",
    message: "",
};

const NAME_MAX_LENGTH = 80;
const MESSAGE_MIN_LENGTH = 10;
const MESSAGE_MAX_LENGTH = 1000;

// Must match CAPTCHA_ACTIONS['contact'] in api-rapideph's PageController.
const CAPTCHA_ACTION = "contact_inquiry";

const NumberFormatCustom = React.forwardRef(
    function NumberFormatCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <PatternFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values, sourceInfo) => {
                    if (values.value) {
                        onChange(values.value);
                    }
                }}
                format="### ### ####"
                allowEmptyFormatting
                mask="-"
            />
        );
    },
);

NumberFormatCustom.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default function ContactUs() {
    const {
        handleSubmit,
        reset,
        setValue,
        setError,
        clearErrors,
        getValues,
        control,
        trigger,
        watch,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: defaultValues,
    });

    const [loading, setLoading] = useState(false);
    const [alertStatus, setAlertStatus] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);

    // Bot protection (GPH-525, same as the franchise form): invisible check, hidden trap field, and how long the form was open.
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
    const turnstileRef = useRef(null);
    const honeypotRef = useRef(null);
    const loadedAtRef = useRef(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        loadedAtRef.current = Date.now();
    }, []);

    const handleCaptchaUnavailable = useCallback(
        () => setCaptchaUnavailable(true),
        [],
    );

    const message = watch("message");

    const onSubmit = async (data) => {
        // A double click can land before the button re-renders as disabled.
        if (submittingRef.current) {
            return;
        }
        // The invisible check usually finishes long before anyone can fill the form.
        // If it can't load at all, send anyway and let the server decide.
        if (!captchaToken && !captchaUnavailable) {
            setAlertStatus(203);
            setAlertMessage(
                "We're still verifying your browser. Please try again in a moment.",
            );
            return;
        }

        submittingRef.current = true;
        setLoading(true);

        const payload = {
            ...data,
            captcha_token: captchaToken,
            website: honeypotRef.current?.value ?? "",
            form_elapsed_ms: Date.now() - loadedAtRef.current,
        };

        try {
            await axios.get("/sanctum/csrf-cookie");
            // The API is on another subdomain, so axios only sends the CSRF header when asked.
            const response = await axios.post(
                process.env.API_URL + `/contact_us`,
                payload,
                { withXSRFToken: true },
            );
            setAlertStatus(response.data.status);
            setAlertMessage(response.data.message);
        } catch (error) {
            const fieldErrors = Object.entries(
                error?.response?.data?.errors ?? {},
            ).filter(([field]) => field in defaultValues);
            if (fieldErrors.length) {
                // Show server-side rule failures on the fields themselves.
                fieldErrors.forEach(([field, messages]) => {
                    setError(field, { type: "server", message: messages[0] });
                });
                setAlertStatus(null);
            } else {
                setAlertStatus(203);
                setAlertMessage(
                    error?.response?.data?.message ??
                        "Connection Failed. Try again.",
                );
            }
        }

        // Tokens are single-use, so fetch a fresh one for any retry.
        turnstileRef.current?.reset();
        submittingRef.current = false;
        setLoading(false);
    };

    const onError = (data) => {
        console.log("Invalid Form!");
    };
    return (
        <div className="max-w-screen-xl mx-auto bg-white">
            <div className="relative grid sm:grid-cols-2 grid-cols-1 sm:px-6 px-3 pt-4 pb-8">
                <div className="px-4 pt-4">
                    <h1 className="text-2xl font-semibold tracking-wide text-rapide-black pb-4">
                        Contact Us
                    </h1>
                    {alertStatus && (
                        <Alert
                            severity={`${alertStatus == 200 ? "success" : "error"}`}
                        >
                            {alertMessage}
                        </Alert>
                    )}
                    <form
                        className="mt-2"
                        onSubmit={handleSubmit(onSubmit, onError)}
                    >
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return (
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="name"
                                        name="name"
                                        label="Full Name"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        error={errors.name != null}
                                        helperText={
                                            errors.name && errors.name?.message
                                        }
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{
                                            maxLength: NAME_MAX_LENGTH,
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                );
                            }}
                            name="name"
                            control={control}
                            rules={{
                                required: "Please enter your full name.",
                                // First and last name, as the server requires.
                                validate: (value) =>
                                    /\S\s+\S/.test(value.trim()) ||
                                    "Please enter your full name.",
                                maxLength: {
                                    value: NAME_MAX_LENGTH,
                                    message: "Please enter your full name.",
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return (
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="text"
                                        id="mobile_no"
                                        name="mobile_no"
                                        label="Mobile Number"
                                        variant="filled"
                                        size="small"
                                        error={errors.mobile_no != null}
                                        helperText={
                                            errors.mobile_no &&
                                            errors.mobile_no?.message
                                        }
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    +63
                                                </InputAdornment>
                                            ),
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                );
                            }}
                            name="mobile_no"
                            control={control}
                            rules={{
                                required: "Please enter your mobile number",
                                minLength: {
                                    value: 10,
                                    message: "Invalid mobile number.",
                                },
                                maxLength: {
                                    value: 10,
                                    message: "Invalid mobile number.",
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return (
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="email"
                                        id="email"
                                        name="email"
                                        label="Email"
                                        variant="filled"
                                        size="small"
                                        error={errors.email != null}
                                        helperText={
                                            errors.email &&
                                            errors.email?.message
                                        }
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailOutlinedIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                );
                            }}
                            name="email"
                            control={control}
                            rules={{
                                required: "Please enter your email address",
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message:
                                        "Please enter a valid email address.",
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return (
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="message"
                                        name="message"
                                        label="Message"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        error={errors.message != null}
                                        helperText={
                                            errors.message
                                                ? errors.message?.message
                                                : `${message.length}/${MESSAGE_MAX_LENGTH}`
                                        }
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{
                                            maxLength: MESSAGE_MAX_LENGTH,
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                );
                            }}
                            name="message"
                            control={control}
                            rules={{
                                required: "Please enter your message",
                                validate: (value) =>
                                    value.trim().length >= MESSAGE_MIN_LENGTH ||
                                    `Please enter at least ${MESSAGE_MIN_LENGTH} characters.`,
                                maxLength: {
                                    value: MESSAGE_MAX_LENGTH,
                                    message: `Message must not exceed ${MESSAGE_MAX_LENGTH} characters.`,
                                },
                            }}
                        />
                        {/* Honeypot: off-screen and hidden from screen readers and the tab order. People never fill it; bots do. */}
                        <div
                            className="absolute -left-[9999px] w-px h-px overflow-hidden"
                            aria-hidden="true"
                        >
                            <label htmlFor="website">Website</label>
                            <input
                                ref={honeypotRef}
                                type="text"
                                id="website"
                                name="website"
                                tabIndex={-1}
                                aria-hidden="true"
                                autoComplete="off"
                                defaultValue=""
                            />
                        </div>
                        <Turnstile
                            ref={turnstileRef}
                            action={CAPTCHA_ACTION}
                            onVerify={setCaptchaToken}
                            onUnavailable={handleCaptchaUnavailable}
                        />
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                            <div className="md:col-start-2 col-start-1">
                                <Button
                                    type="submit"
                                    loading={loading}
                                    label="Submit"
                                    llabel="Submitting..."
                                />
                            </div>
                        </div>
                    </form>
                </div>
                <div className="px-4 pt-4">
                    <div
                        className="w-full"
                        dangerouslySetInnerHTML={{
                            __html: `<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3861.613456480296!2d121.00912601533375!3d14.564083681873951!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397c90abb2b69db%3A0xb1e57cf5fbe7dcc8!2sRapid%C3%A9%201%20San%20Antonio%20Makati%20-%20Auto%20Service%2C%20Car%20Repair!5e0!3m2!1sen!2sph!4v1667381367326!5m2!1sen!2sph" width="100%" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`,
                        }}
                    ></div>
                </div>
            </div>
            <div className="max-w-screen-lg mx-auto grid md:grid-cols-3 grid-cols-1 gap-4 pt-4 pb-14">
                <div className="flex flex-row">
                    <div>
                        <CallIcon
                            fontSize="large"
                            className="text-rapide-yellow w-14"
                        />
                    </div>
                    <div>
                        <h1 className="font-bold text-xl">Call Us</h1>
                        <a className="font-bold block" href="tel:+639660619979">
                            09985-RAPID8 / 0998-572-7438 (Smart)
                        </a>
                        {/* <a className="font-bold" href="tel:+639192694103">0919 269 4103 (Smart)</a> */}
                    </div>
                </div>
                <div className="flex flex-row">
                    <div>
                        <AccessTimeFilledIcon
                            fontSize="large"
                            className="text-rapide-yellow w-14"
                        />
                    </div>
                    <div>
                        <h1 className="font-bold text-xl">Office Hours</h1>
                        <p>Main Office: Monday to Sunday</p>
                        <p>8:30 AM - 5:30 PM</p>
                    </div>
                </div>
                <div className="flex flex-row">
                    <div>
                        <LocationOnIcon
                            fontSize="large"
                            className="text-rapide-yellow w-14"
                        />
                    </div>
                    <div>
                        <h1 className="font-bold text-xl">Location</h1>
                        <p>
                            1166 Chino Roces Avenue, Corner Estrella Avenue,
                            Makati
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
